import { useState } from "react";
import { AlertTriangle, Lock, LockOpen } from "lucide-react";
import { toast } from "sonner";
import { nomeDoMes } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { usePermissao } from "@/lib/use-permissao";
import { reais, type MesDoFechamento } from "@/lib/dinheiro";
import { cn } from "@/lib/cn";
import { FecharMesDialog } from "./FecharMesDialog";

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const dataHora = (d: string | Date) => new Date(d).toLocaleDateString("pt-BR");

function Linha({ m, pode, dono, onFechar }: { m: MesDoFechamento; pode: boolean; dono: boolean; onFechar: () => void }) {
  const utils = trpc.useUtils();
  const [confirmando, setConfirmando] = useState(false);
  const reabrir = trpc.financeiro.fechamento.reabrir.useMutation({
    onSuccess: () => {
      toast.success(`${capital(nomeDoMes(m.competencia))} reaberto.`);
      utils.financeiro.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  const f = m.fechamento;
  const numeros = f?.foto ?? m.vivo;
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
      {f ? <Lock className="h-4 w-4 shrink-0 text-zinc-500" /> : <LockOpen className="h-4 w-4 shrink-0 text-zinc-300" />}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-zinc-900 dark:text-foreground">{capital(nomeDoMes(m.competencia))}</p>
        <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
          {f ? `Fechado em ${dataHora(f.fechadoEm)}${f.fechadoPor ? ` por ${f.fechadoPor}` : ""}` : m.atual ? "Mês em andamento" : "Aberto"}
          {" · "}receita {reais(numeros.receitaCents)}
        </p>
        {f?.mudouDepois && (
          <p className="mt-0.5 flex items-center gap-1 text-[11px] font-medium text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-3 w-3" /> Mudou depois do fechamento (serviço reaberto, data antiga ou regra do cálculo): hoje o resultado seria {reais(m.vivo.resultadoCents)}.
          </p>
        )}
      </div>
      <span className={cn("w-32 text-right text-sm font-semibold tabular-nums", numeros.resultadoCents >= 0 ? "text-emerald-600" : "text-red-600")}>{reais(numeros.resultadoCents)}</span>
      <div className="flex w-36 justify-end gap-1">
        {!f && pode && (
          <button type="button" onClick={onFechar} className="rounded-md border border-zinc-200 px-2.5 py-1 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted">
            Fechar
          </button>
        )}
        {f && dono && !confirmando && (
          <button type="button" onClick={() => setConfirmando(true)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-muted">
            Reabrir
          </button>
        )}
        {confirmando && (
          <>
            <button type="button" onClick={() => reabrir.mutate({ competencia: m.competencia })} className="rounded-md bg-red-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-red-700">
              Reabrir
            </button>
            <button type="button" onClick={() => setConfirmando(false)} className="rounded-md px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-100 dark:hover:bg-muted">
              Cancelar
            </button>
          </>
        )}
      </div>
    </li>
  );
}

/** Os últimos 12 meses: fechar trava o mês e guarda o resultado oficial; reabrir é do proprietário. */
export function FechamentoDosMeses() {
  const { user } = useAuth();
  const pode = usePermissao("finance.manage");
  const dono = user?.activeOrganization?.role === "OWNER";
  const meses = trpc.financeiro.fechamento.listar.useQuery().data ?? [];
  const [fechando, setFechando] = useState<string | null>(null);

  return (
    <section className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card">
      <header className="border-b border-zinc-100 bg-zinc-50/60 px-4 py-2.5 dark:border-border dark:bg-muted/30">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Fechamento do mês</h3>
        <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">Mês fechado não aceita lançamento novo nem edição; contas dele ainda podem ser pagas. Só o proprietário reabre.</p>
      </header>
      <ul className="divide-y divide-zinc-100 dark:divide-border/50">
        {meses.map((m) => (
          <Linha key={m.competencia} m={m} pode={pode} dono={dono} onFechar={() => setFechando(m.competencia)} />
        ))}
      </ul>
      <FecharMesDialog competencia={fechando} onFechar={() => setFechando(null)} />
    </section>
  );
}
